import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import bulkEmailService, { BulkEmailStatus, BulkEmailHistoryJob, BulkEmailQueueStats } from '../../services/bulkEmailService';

const BulkEmailMonitoring: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  
  const [currentStatus, setCurrentStatus] = useState<BulkEmailStatus | null>(null);
  const [history, setHistory] = useState<BulkEmailHistoryJob[]>([]);
  const [queueStats, setQueueStats] = useState<BulkEmailQueueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [selectedJob, setSelectedJob] = useState<BulkEmailHistoryJob | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (eventId) {
      fetchAllData();
    }
  }, [eventId]);

  // Polling for real-time updates
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (polling && currentStatus?.status === 'processing') {
      interval = setInterval(() => {
        fetchCurrentStatus();
        fetchQueueStats();
      }, 2000); // Poll every 2 seconds
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [polling, currentStatus?.status]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        fetchCurrentStatus(),
        fetchHistory(),
        fetchQueueStats(),
      ]);
    } catch (err) {
      console.error('Error fetching bulk email data:', err);
      setError('Failed to load bulk email monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentStatus = async () => {
    if (!eventId) return;
    
    try {
      const response = await bulkEmailService.getBulkEmailResendStatus(eventId);
      if (response.success) {
        setCurrentStatus(response.data);
        
        // Start polling if job is processing
        if (response.data.status === 'processing') {
          setPolling(true);
        } else {
          setPolling(false);
        }
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setCurrentStatus(null);
      } else {
        throw err;
      }
    }
  };

  const fetchHistory = async () => {
    if (!eventId) return;
    
    try {
      const response = await bulkEmailService.getBulkEmailResendHistory(eventId, 1, 20);
      if (response.success) {
        setHistory(response.data.jobs);
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        throw err;
      }
    }
  };

  const fetchQueueStats = async () => {
    try {
      const response = await bulkEmailService.getBulkEmailQueueStats();
      if (response.success) {
        setQueueStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching queue stats:', err);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'primary';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon />;
      case 'processing':
        return <ScheduleIcon />;
      case 'failed':
        return <ErrorIcon />;
      default:
        return <EmailIcon />;
    }
  };

  const handleViewDetails = (job: BulkEmailHistoryJob) => {
    setSelectedJob(job);
    setDetailsOpen(true);
  };

  if (!eventId) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Event ID is required for bulk email monitoring
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Bulk Email Monitoring
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAllData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate(-1)}
          >
            Back to Event
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Queue Statistics */}
      {queueStats && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <ScheduleIcon color="primary" />
                  <Typography variant="h6">
                    {queueStats.waiting}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Waiting
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <TrendingUpIcon color="info" />
                  <Typography variant="h6">
                    {queueStats.active}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Active
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="h6">
                    {queueStats.completed}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <ErrorIcon color="error" />
                  <Typography variant="h6">
                    {queueStats.failed}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Failed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Current Status */}
      {currentStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Job Status
            </Typography>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    icon={getStatusIcon(currentStatus.status)}
                    label={currentStatus.status.toUpperCase()}
                    color={getStatusColor(currentStatus.status) as any}
                    variant="outlined"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Job ID: {currentStatus.jobId}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" align="right">
                  {currentStatus.emailsSent} / {currentStatus.totalOrders} sent
                </Typography>
              </Grid>
            </Grid>

            {currentStatus.status === 'processing' && (
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">
                    Progress: {currentStatus.progress}%
                  </Typography>
                  {currentStatus.estimatedTimeRemaining && (
                    <Typography variant="body2" color="text.secondary">
                      ~{formatDuration(currentStatus.estimatedTimeRemaining)} remaining
                    </Typography>
                  )}
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={currentStatus.progress} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">
                  Total Orders
                </Typography>
                <Typography variant="h6">
                  {currentStatus.totalOrders}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" color="success.main">
                  Emails Sent
                </Typography>
                <Typography variant="h6" color="success.main">
                  {currentStatus.emailsSent}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" color="error.main">
                  Failed
                </Typography>
                <Typography variant="h6" color="error.main">
                  {currentStatus.emailsFailed}
                </Typography>
              </Grid>
            </Grid>

            {currentStatus.duration && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Duration: {formatDuration(currentStatus.duration)}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* History Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Bulk Email History
          </Typography>
          
          {history.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell align="right">Total Orders</TableCell>
                    <TableCell align="right">Sent</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(job.status)}
                          label={job.status.toUpperCase()}
                          color={getStatusColor(job.status) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(job.started_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {job.duration_seconds ? formatDuration(job.duration_seconds) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {job.total_orders}
                      </TableCell>
                      <TableCell align="right">
                        <Typography color="success.main">
                          {job.emails_sent}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography color="error.main">
                          {job.emails_failed}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {job.created_by}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(job)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={4}>
              <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No bulk email history found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bulk email jobs will appear here once they are started.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Job Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Job Details
        </DialogTitle>
        <DialogContent>
          {selectedJob && (
            <List>
              <ListItem>
                <ListItemText
                  primary="Event"
                  secondary={selectedJob.event_name}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Status"
                  secondary={
                    <Chip
                      icon={getStatusIcon(selectedJob.status)}
                      label={selectedJob.status.toUpperCase()}
                      color={getStatusColor(selectedJob.status) as any}
                      size="small"
                      variant="outlined"
                    />
                  }
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Started At"
                  secondary={new Date(selectedJob.started_at).toLocaleString()}
                />
              </ListItem>
              {selectedJob.completed_at && (
                <>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Completed At"
                      secondary={new Date(selectedJob.completed_at).toLocaleString()}
                    />
                  </ListItem>
                </>
              )}
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Duration"
                  secondary={selectedJob.duration_seconds ? formatDuration(selectedJob.duration_seconds) : 'N/A'}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Total Orders"
                  secondary={selectedJob.total_orders}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Emails Sent"
                  secondary={
                    <Typography color="success.main">
                      {selectedJob.emails_sent}
                    </Typography>
                  }
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Emails Failed"
                  secondary={
                    <Typography color="error.main">
                      {selectedJob.emails_failed}
                    </Typography>
                  }
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Created By"
                  secondary={selectedJob.created_by}
                />
              </ListItem>
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BulkEmailMonitoring;
